import {
  Address,
} from '../../models'
import BaseModel from './base'
import ContactService from '../../services/ContactServices'
import ContactServices from '../../services/ContactServices'
import { MessageType } from '../../models/message'
import { ModelUtils } from '../model_utils'
import {SendMessageTypes} from "./ConversationChannel";
import { Utils } from '../../util/utils'
import cloneDeep from "lodash-es/cloneDeep"
import firebase from 'firebase/compat/app'
import { getCountryDateFormat } from '@/models'
import isArray from "lodash-es/isArray"
import isEmpty from "lodash-es/isEmpty"
import isEqual from "lodash-es/isEqual"
import moment from 'moment-timezone'
import { parsePhoneNumber } from "libphonenumber-js";
import remove from "lodash-es/remove"
import store from '../../store'
import vuex from '../../store'

export interface ContactCustomField {
  id: string
  field_value: string
  value: string
  fieldKey: string
  fieldValueString: string
  fieldValueNumber: any
  fieldValueArray: number
  fieldValueDate: any
  display: any
}

export type Type = 'lead' | 'customer'

export interface IContactOpportunity {
  id: string
  pipelineId: string
  pipelineStageId: string
  monetaryValue: number
  status: string
}

export interface IFormattedOpportunity extends IContactOpportunity {
  pipeline?: string
  stage?: string
}

export interface PhoneInfo {
  valid: boolean
  smsCapable?: boolean
  type?: string
  carrierName?: string
}

export interface DialogFlowBotInfo {
  agent: string
  projectId: string
  calendarId: string
  uuid?: string
  welcomeTemplate: string
  thankyouTemplate: string
  timeout: number
  elizaSessionId: string
  logicalElizaId?: string
}

export interface MultipleFacebookDetails {
  id?: string
  page_id?: string
}

export interface FaceBookMetaData {
  id?: string
  multiple?: MultipleFacebookDetails[]
  last_page_id?: string,
  ids?: string[]
}
export interface MultiplePageDetails {
  id?: string
  page_id?: string
}

export interface InstagramMetaData {
  multiple?: MultiplePageDetails[]
  last_page_id?: string
  ids?: string[]
}

export interface IContact {
  contactName: string
  timezone: string
  locationId: string
  dateAdded: moment.Moment
  dateUpdated: moment.Moment
  deleted: boolean
  dirty: boolean
  internalSource: { [key: string]: any }
  type: Type
  tags: string[]
  customFields: ContactCustomField[]
  state: string | undefined
  postalCode: string | undefined
  phone: string
  parent: string
  firstName: string
  lastName: string
  name: string
  email: string
  country: string | undefined
  companyName: string
  city: string | undefined
  billTo: string
  addressGeo: firebase.firestore.GeoPoint | undefined
  address1: string | undefined
  source: string
  dateOfBirth: moment.Moment | undefined
  birthMonth: number
  birthDay: number
  gender: string
  googleData: { [key: string]: any }
  ssn: string
  website: string
  dnd: boolean
  assignedTo: string
  assignedToDisplay: string
  importId: string
  lastConversationId: string
  lastConversationMessageType: MessageType
  lastActivity: moment.Moment
  lastAppointment: moment.Moment
  lastNote: string
  attachments: string[]
  fingerprint: string
  validEmail: boolean
  bounceEmail: boolean
  unsubscribeEmail: boolean
  instagramId: string
  address: Address
  fullAddressLine: string
  addressLine1: string
  profilePhoto: string
  clio: any
  facebook: any
  poolId: string
  gclid: string
  sourceType: string
  keyword: string
  opportunities: Array<IContactOpportunity>
  offers: string[]
  products: string[]
  sessionId: string
  phoneInfo: PhoneInfo
  dialogflowBotInfo: DialogFlowBotInfo
  activeWorkflows?: string[]
  activeWorkflows2?: string[]
  finishedWorkflows?: string[]
  finishedWorkflows2?: string[]
  attributionSource: { [key: string]: any }
  lastAttributionSource: { [key: string]: any }
  businessId : string,
  skipEmailVerification: boolean
  isValidWhatsapp?: boolean
  scoring: { [key: string]: number },
  instagram: any
}

export enum IDndStatus {
  active = 'active',
  inactive = 'inactive',
  permanent = 'permanent'
}

export interface IDndSetting {
  status: IDndStatus
  message: string
  code: string
}

export enum IInboundDndStatus {
  active = 'active',
  inactive = 'inactive'
}

export enum PhoneLabels {
  HOME = 'Home',
  WORK = 'Work',
  MOBILE = 'Mobile',
  LANDLINE = 'Landline',
}


interface IInboundDndSettingValues {
  status: IInboundDndStatus
  message?: string
}

export interface IInboundDndSetting {
  all?: IInboundDndSettingValues
}

export default class Contact extends BaseModel {
  public static TYPE_CUSTOMER = 'customer'
  public static TYPE_LEAD = 'lead'

  static requestBodyWhiteList: string[] = [
    'email',
    'phone',
    'phoneLabel',
    'firstName',
    'lastName',
    'dateOfBirth',
    'address1',
    'city',
    'state',
    'country',
    'postalCode',
    'companyName',
    'website',
    'tags',
    'source',
    'customFields',
    'type',
    'timezone',
    'dnd',
    'inboundDndSettings',
    'sessionId',
    'facebookId',
    'fingerprint',
    'assignedTo',
    'profilePhoto',
    'attributionSource',
    'dirty',
    'dialogflowBotInfo',
    'parent',
    'billTo',
    'googleData',
    'ssn',
    'importId',
    'attachments',
    'bounceEmail',
    'unsubscribeEmail',
    'instagramId',
    'poolId',
    'gclid',
    'sourceType',
    'keyword',
    'offers',
    'products',
    'phoneInfo',
    'lastAttributionSource',
    'dndSettings',
    'businessId',
    'additionalEmails',
    'additionalPhones',
    'isValidWhatsapp',
    'scoring',
    'createdBy',
    'skipEmailVerification',
    'followers',
    'instagram'
  ]

  // Methods
  constructor(data: IContact) {
        super(Contact.requestBodyWhiteList, data)

    if (data && typeof data.dateOfBirth === 'number') {
      this.dateOfBirth = moment(data.dateOfBirth)
    }

    if (['Etc/Greenwich', 'GMT'].includes(this.timezone)) {
      this.timezone = 'UTC'
    }

    if (data && data.contactName) {
      delete this.data.contactName
      delete this.updatedData.contactName
    }
  }

  async save(params = {
    skipTrigger: false
  }): Promise<Contact> {
    return new Promise(async (resolve, reject) => {
      try {
        this.dirty = true
        const tagsNotChanged = (isEqual(this.oldData.tags?.sort() , this.tags?.sort()))
        const data =
            Contact.requestBodyWhiteList.length === 0
              ? this.updatedData
              : Contact.requestBodyWhiteList.reduce((acc, cv) => {
                 if (cv === 'tags' && tagsNotChanged ) return acc
                  acc[cv] = this.updatedData[cv]
                  return acc
                }, {})

        data['skipTrigger'] = params.skipTrigger
        if(data['customFields']){
          data['customFields'] = data['customFields'].map((cf: any)=>{
            return {
              id: cf.id,
              field_value: cf.field_value === undefined ? cf.value : cf.field_value
            }
          })
        }

        const succeededProcedure = async () => {
          if (this.type !== 'lead' && vuex.getters['oauth2/updateContact']) {
            await this.syncOAuth(this.id)
              .catch(function (error) {
                console.error(error)
              })
          }
        }

        if (this.newRecord) {
          ContactService.create(data)
          .then(response => {
            this.id = response.contact.id
            this.newRecord = false
            succeededProcedure()
            resolve(new Contact(response.contact))
          })
          .catch(err => {
            reject(err.response?.data?.message)
          })
        } else {
          ContactService.update(this.id, data)
          .then(response => {
            succeededProcedure()
            resolve(new Contact(response.contact))
          })
          .catch(err => {
            reject(err.response?.data?.message)
          })
        }
      } catch (err) {
        reject(err.response?.data?.message)
      }
    })
  }

  static async getById(id: string): Promise<Contact> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await ContactServices.read(id)
                resolve(new Contact({validEmail:null,dndSettings:{Call:null},...response.contact}))
      } catch (error) {
        reject(error)
      }
    })
  }

  async syncOAuth(id: string): Promise<Contact> {
    return await ContactServices.syncOAuth(id)
  }

  static async validateEmail(id: string): Promise<Contact> {
    const response = await ContactServices.validateEmail(id)
    return new Contact(response.contact)
  }

  private static colours: string[] = [
    '#188bf6',
    '#1976d2',
    '#e93d3d',
    '#ffbc00',
    '#37ca37',
    '#876cff',
    '#17cfbc',
    '#ff3e7f',
    '#ff7402',
    '#7be43b',
  ]

  get scoring(): { [key: string]: any } {
    if(!isArray(this.data.scoring)) return this.data.scoring || {}
    const scoring = {}
    this.data.scoring?.forEach(s => scoring[s.id] = s.score)
    return scoring
  }

  set scoring(score: {[key: string]: number}) {
    this.data.scoring = score
  }

  set stage(value: string) {
    this.data.stage = value
    this.updatedData.stage = value
  }

  get stage(): string {
    return this.data.stage
  }

  set value(value: string) {
    this.data.value = value
    this.updatedData.value = value
  }

  get value(): string {
    return this.data.value
  }

  get id(): string {
    return this.data.id
  }

  set id(value: string) {
    this.data.id = value
    this.updatedData.id = value
  }

  get timezone(): string | undefined {
    return this.data.timezone
  }

  set timezone(timezone: string | undefined) {
    this.data.timezone = timezone
    this.updatedData.timezone = timezone
  }

  get locationId(): string {
    return this.data.locationId
  }

  set locationId(locationId: string) {
    this.data.locationId = locationId
    this.updatedData.locationId = locationId
  }
  get dateAdded(): moment.Moment {
    return this.data['dateAdded']
      ? typeof this.data['dateAdded'] === 'number' || 'string'
        ? moment(this.data['dateAdded'])
        : moment(this.data['dateAdded'].toMillis())
      : undefined
  }

  set dateAdded(dateAdded: moment.Moment) {
    this.data['dateAdded'] = firebase.firestore.Timestamp.fromMillis(
      dateAdded.valueOf()
    )
    this.updatedData['dateAdded'] = firebase.firestore.Timestamp.fromMillis(
      dateAdded.valueOf()
    )
  }
  get dateUpdated(): moment.Moment {
    return this.data['dateUpdated']
      ? typeof this.data['dateUpdated'] === 'number' || 'string'
        ? moment(this.data['dateUpdated'])
        : moment(this.data['dateUpdated'].toMillis())
      : undefined
  }

  set dateUpdated(dateUpdated: moment.Moment) {
    this.data['dateUpdated'] = firebase.firestore.Timestamp.fromMillis(
      dateUpdated.valueOf()
    )
    this.updatedData['dateUpdated'] = firebase.firestore.Timestamp.fromMillis(
      dateUpdated.valueOf()
    )
  }

  get businessId() :string{
    return this.data.businessId
  }
  set businessId(id : string) {
     this.data.businessId = id
    this.updatedData.businessId = id
  }

  get businessName() :string{
    return this.data.businessName
  }
  set businessName(businessName: string) {
     this.data.businessName = businessName
    this.updatedData.businessName = businessName
  }

  get deleted(): boolean {
    return this.data.deleted
  }

  set deleted(deleted: boolean) {
    this.data.deleted = deleted
    this.updatedData.deleted = deleted
  }

  get dirty(): boolean {
    return this.data.dirty
  }

  set dirty(dirty: boolean) {
    this.data.dirty = dirty
    this.updatedData.dirty = dirty
  }

  get internalSource(): { [key: string]: any } {
    return this.data.internalSource
  }

  set internalSource(internalSource: { [key: string]: any }) {
    this.data.internalSource = internalSource
    this.updatedData.internalSource = internalSource
  }

  get type(): Type {
    return this.data.type
  }

  set type(type: Type) {
    this.data.type = type
    this.updatedData.type = type
  }

  get tags(): string[] {
    if (isEmpty(this.data.tags) || !(this.data.tags instanceof Array))
      return []
    return this.data.tags
  }

  get skipEmailVerification():boolean {
    return this.data.skipEmailVerification ?? false
  }

  set skipEmailVerification(skipEmailVerification: boolean) {
    this.data.skipEmailVerification = skipEmailVerification
    this.updatedData.skipEmailVerification = skipEmailVerification
  }

  set tags(tags: string[]) {
    this.data.tags = tags
    this.updatedData.tags = tags
  }

  public addTags(tags: string[]) {
    tags.forEach(tag => {
      this.addTag(tag)
    })
  }

  public removeTags(tags: string[]) {
    tags.forEach(tag => {
      this.removeTag(tag)
    })
  }

  public addTag(tag: string) {
    if (this.tags.indexOf(tag.toLowerCase()) === -1) {
      this.tags.push(tag.toLowerCase())
    }
  }

  public removeTag(tag: string) {
    if (this.tags.indexOf(tag) !== -1) {
      this.tags.splice(this.tags.indexOf(tag), 1)
    }
  }

  get customFields(): ContactCustomField[] {
    if (!this.data.customFields) this.data.customFields = []

    return this.data.customFields.map((cf: ContactCustomField) => {
      if (cf.fieldValueDate) {
        const dt = moment(cf.fieldValueDate)
        return {
          id: cf.id,
          value: dt.format('YYYY-MM-DD'),
        } as ContactCustomField
      } else {
        return {
          id: cf.id,
          field_value:
            cf.fieldValueArray ??
            cf.fieldValueNumber ??
            cf.fieldValueString ??
            cf.value ??
            cf.field_value
        } as ContactCustomField
      }
    })
  }

  set customFields(newItems: ContactCustomField[]) {
    this.data.customFields = newItems
    this.updatedData.customFields = newItems
  }
  get createdBy(){

    return this.data?.createdBy
  }

  public getCustomFieldValue(key: string) {
    // NOTE:: Fetching customFields from the getter formats it. Hence we need the unformatted one as we need to transform the dateField.
    // Hence using this.data.customFields rather than this.customFields
    if (this.data.customFields && this.data.customFields.length > 0) {
      const item = this.data.customFields.find(
        (a: ContactCustomField) => a && a.id === key
      )
      if (!item) return null

      if (
        (item.fieldValueDate && typeof item.fieldValueDate === 'number')
      ) {
        return moment(item.fieldValueDate)
          .utc()
          .format(getCountryDateFormat('month date, year'))
      } else
        return `${
          item.fieldValueNumber ||
          item.fieldValueArray ||
          item.fieldValueString ||
          item.value ||
          item.field_value ||
          ''
        }`

    }
    return null
  }

  public addCustomFields(customFields: ContactCustomField[]) {
    customFields.forEach(field => {
      this.addCustomField(field)
    })
  }

  public removeCustomFields(customFields: ContactCustomField[]) {
    customFields.forEach(field => {
      this.removeCustomField(field)
    })
  }

  public addCustomField(customFields: ContactCustomField) {
    const check = this.customFields.find(x => x.id === customFields.id)
    if (!check) {
      this.customFields.push(customFields)
    }
  }

  public removeCustomField(customFields: ContactCustomField) {
    const index = this.customFields.findIndex(x => x.id === customFields.id)
    if (index && index !== -1) {
      this.customFields.splice(index, 1)
    }
  }

  get state(): string | null {
    return this.data.state
  }

  set state(state: string | null) {
    this.data.state = state
    this.updatedData.state = state
  }

  get postalCode(): string | null {
    return this.data.postalCode
  }

  set postalCode(postalCode: string | null) {
    this.data.postalCode = postalCode
    this.updatedData.postalCode = postalCode
  }

  get phone(): string {
    return this.data.phone
  }

  set phone(phone: string) {
    this.data.phone = phone
    this.updatedData.phone = phone
  }


  get phoneLabel(): string {
    return this.data.phoneLabel
  }

  set phoneLabel(phoneLabel: string) {
    this.data.phoneLabel = phoneLabel
    this.updatedData.phoneLabel = phoneLabel
  }


  get parent(): string {
    return this.data.parent
  }

  set parent(parent: string) {
    this.data.parent = parent
    this.updatedData.parent = parent
  }

  get lastName(): string {
    return this.data.lastName
  }

  set lastName(lastName: string) {
    this.data.lastName = lastName
    this.updatedData.lastName = lastName
  }

  get firstName(): string {
    return this.data.firstName
  }

  set firstName(firstName: string) {
    this.data.firstName = firstName
    this.updatedData.firstName = firstName
  }

  get name(): string {
    return [this.firstName, this.lastName].filter(val => val).join(' ')
  }

  set name(name: string) {
    const stringIsATitle = (e: string) => {
      e = e.toLowerCase()
      if (
        e === 'mr.' ||
        e === 'dr.' ||
        e === 'er.' ||
        e === 'ms.' ||
        e === 'mrs.' ||
        e === 'mr' ||
        e === 'dr' ||
        e === 'er' ||
        e === 'ms' ||
        e === 'mrs'
      ) {
        return true
      }
      return false
    }

    if (name) {
      let hasTitle = false
      const parts = name.match(/\S+/g) || []
      if (parts.length >= 1) {
        if (stringIsATitle(parts[0])) {
          this.firstName = parts[0] + ' ' + parts[1]
          hasTitle = true
        } else {
          this.firstName = parts[0]
        }
      }
      if (parts.length >= 2) {
        if (hasTitle) {
          parts.splice(0, 2)
        } else {
          parts.splice(0, 1)
        }
        this.lastName = parts.join(' ')
      }
    }
  }

  get email(): string {
    return this.data.email
  }

  set email(email: string) {
    this.data.email = email
    this.updatedData.email = email
    if (email) {
      this.data.bounceEmail = false
      this.updatedData.bounceEmail = false

      this.data.unsubscribeEmail = false
      this.updatedData.unsubscribeEmail = false
    }
  }

  get country(): string | undefined {
    return this.data.country
  }

  set country(country: string | undefined) {
    this.data.country = country
    this.updatedData.country = country
  }

  get companyName(): string | null {
    return this.data.companyName
  }

  set companyName(companyName: string | null) {
    this.data.companyName = companyName
    this.updatedData.companyName = companyName
  }

  get city(): string | null {
    return this.data.city
  }

  set city(city: string | null) {
    this.data.city = city
    this.updatedData.city = city
  }

  get billTo(): string {
    return this.data.billTo
  }

  set billTo(billTo: string) {
    this.data.billTo = billTo
    this.updatedData.billTo = billTo
  }

  get addressGeo(): firebase.firestore.GeoPoint | undefined {
    return this.data.addressGeo
  }

  set addressGeo(addressGeo: firebase.firestore.GeoPoint | undefined) {
    this.data.addressGeo = addressGeo
    this.updatedData.addressGeo = addressGeo
  }

  get source(): string {
    return this.data.source
  }

  set source(source: string) {
    this.data.source = source
    this.updatedData.source = source
  }

  get dateOfBirth(): moment.Moment {

    return this.data['dateOfBirth']
      ? (typeof this.data['dateOfBirth'] === 'number' || 'string' || 'object')
        ? (moment.utc(this.data['dateOfBirth']).isValid() ? moment.utc(this.data['dateOfBirth']) : undefined)
        : moment(this.data['dateOfBirth'].toMillis())
      : undefined
  }

  set dateOfBirth(dateOfBirth: moment.Moment) {
    this.data['dateOfBirth'] = moment(dateOfBirth)

    this.updatedData['dateOfBirth'] = moment(dateOfBirth)
  }

  get birthMonth(): number {
    return this.data.birthMonth
  }

  set birthMonth(birthMonth: number) {
    this.data.birthMonth = birthMonth
    this.updatedData.birthMonth = birthMonth
  }

  get birthDay(): number {
    return this.data.birthDay
  }

  set birthDay(birthDay: number) {
    this.data.birthDay = birthDay
    this.updatedData.birthDay = birthDay
  }

  get gender(): string {
    return this.data.gender
  }

  set gender(gender: string) {
    this.data.gender = gender
    this.updatedData.gender = gender
  }

  get googleData(): { [key: string]: any } {
    if (!this.data['googleData']) {
      this.data['googleData'] = {}
    }
    return this.data['googleData']
  }

  set googleData(googleData: { [key: string]: any }) {
    this.data['googleData'] = googleData
    this.updatedData['googleData'] = googleData
  }

  get ssn(): string {
    return this.data.ssn
  }

  set ssn(ssn: string) {
    this.data.ssn = ssn
    this.updatedData.ssn = ssn
  }

  get website(): string | null {
    return this.data.website
  }

  set website(website: string | null) {
    this.data.website = website
    this.updatedData.website = website
  }

  get dnd(): boolean {
    return this.data['dnd'] || false
  }

  set dnd(dnd: boolean) {
    this.data['dnd'] = dnd
    this.updatedData['dnd'] = dnd
  }

  get inboundDndSettings(): IInboundDndSetting {
    return this.data['inboundDndSettings']
  }

  set inboundDndSettings(inboundDndSettings: IInboundDndSetting) {
    this.data['inboundDndSettings'] = inboundDndSettings
    this.updatedData.inboundDndSettings = inboundDndSettings
  }

  get assignedTo(): string {
    return this.data.assignedTo
  }

  set assignedTo(assignedTo: string) {
    this.data.assignedTo = assignedTo
    this.updatedData.assignedTo = assignedTo
  }

  get assignedToDisplay(): string {
    return Utils.getUserName(this.assignedTo)
  }

  get importId(): string {
    return this.data['importId']
  }

  set importId(importId: string) {
    this.data['importId'] = importId
    this.updatedData['importId'] = importId
  }

  get lastConversationId(): string {
    return this.data.lastConversationId
  }

  set lastConversationId(conversationId: string) {
    this.data.lastConversationId = conversationId
    this.updatedData.lastConversationId = conversationId
  }

  get lastConversationMessageType(): MessageType {
    return this.data.lastConversationMessageType
  }

  set lastConversationMessageType(messageType: MessageType) {
    this.data.lastConversationMessageType = messageType
    this.updatedData.lastConversationMessageType = messageType
  }

  get lastActivity(): moment.Moment {
    return ModelUtils.getDateOnRaw(this.data, 'lastActivity')
  }

  set lastActivity(lastActivity: moment.Moment) {
    ModelUtils.setDateOnRaw(this.data, 'lastActivity', lastActivity)
    ModelUtils.setDateOnRaw(this.updatedData, 'lastActivity', lastActivity)
  }

  get lastAppointment(): moment.Moment {
    return ModelUtils.getDateOnRaw(this.data, 'lastAppointment')
  }

  set lastAppointment(lastAppointment: moment.Moment) {
    ModelUtils.setDateOnRaw(this.data, 'lastAppointment', lastAppointment)
    ModelUtils.setDateOnRaw(
      this.updatedData,
      'lastAppointment',
      lastAppointment
    )
  }

  get lastNote(): string {
    return this.data.lastNote || this.data.lastNote
  }

  set lastNote(lastNote: string) {
    this.data.lastNote = lastNote
    this.updatedData.lastNote = lastNote
  }

  get attachments(): string[] {
    if (!this.data.attachments) {
      this.data.attachments = []
    }
    return this.data.attachments
  }

  set attachments(attachments: string[]) {
    this.data.attachments = attachments
    this.updatedData.attachments = attachments
  }

  public remove_attachment(url) {
    remove(this.data.attachments, url)
  }

  get fingerprint(): string {
    return this.data['fingerprint']
  }
  set fingerprint(fingerprint: string) {
    this.data['fingerprint'] = fingerprint
    this.updatedData['fingerprint'] = fingerprint
  }

  get validEmail(): boolean {
    return this.data.validEmail
  }

  set validEmail(validEmail: boolean) {
    this.data.validEmail = validEmail
    this.updatedData.validEmail = validEmail
  }

  get bounceEmail(): boolean {
    return this.data['bounceEmail'] || false
  }

  set bounceEmail(bounceEmail: boolean) {
    this.data['bounceEmail'] = bounceEmail
    this.updatedData['bounceEmail'] = bounceEmail
  }

  get unsubscribeEmail(): boolean {
    return this.data['unsubscribeEmail'] || false
  }

  set unsubscribeEmail(bounceEmail: boolean) {
    this.data['unsubscribeEmail'] = bounceEmail
    this.updatedData['unsubscribeEmail'] = bounceEmail
  }

  get instagramId(): string {
    return this.data.instagramId
  }

  set instagramId(instagramId: string) {
    this.data.instagramId = instagramId
    this.updatedData.instagramId = instagramId
  }

  set address(address: Address) {
    this.address1 = address.address1
    this.city = address.city
    this.state = address.state
    this.country = address.country
    this.postalCode = address.postalCode
    this.addressGeo = address.addressGeo
  }

  get address() {
    return new Address({
      address1: this.address1,
      city: this.city,
      state: this.state,
      country: this.country,
      postalCode: this.postalCode,
      addressGeo: this.addressGeo,
    })
  }

  get fullAddressLine(): string {
    let address: string = ''
    const addr1 =
      this.address1 ||
      this.data
        .address_1 /* backward compatibility with what it is called in es index  */
    if (addr1) {
      address += addr1 + ', '
    }
    if (this.city) {
      address += this.city + ' '
    }
    if (this.state) {
      address += this.state + ' '
    }
    if (this.postalCode) {
      address += this.postalCode
    }
    return address
  }

  get addressLine1(): string {
    // in elastic search it is stored as address_1 while in firestore it is stored as address1
    if (!this.data) return ''
    return this.data.address_1 || this.address1
  }

  get fullName(): string {
    return Contact.toTitleCase(
      [this.firstName, this.lastName].filter(val => val).join(' ')
    )
  }

  set fullName(name: string) {
    if (name) {
      const parts = name.match(/\S+/g) || []
      if (parts.length >= 1) {
        this.firstName = parts[0]
      }
      if (parts.length >= 2) {
        parts.splice(0, 1)
        this.lastName = parts.join(' ')
      } else {
        this.lastName = ''
      }
    }
  }

  get displayName() {
    if (this.firstName || this.lastName)
      return Contact.toTitleCase(
        [this.firstName, this.lastName].filter(d => d).join(' ')
      )
    if (this.email) return this.email
    if (this.phone) {
      try {
        return parsePhoneNumber(this.phone).formatNational();
      } catch {}
    }
    if (this.companyName) return this.companyName
    return ''
  }

  public static toTitleCase(str: string) {
    if (str) {
      const words = []
      str.split(' ').map(value => {
        if (value.indexOf('-') > -1) {
          const chunkstr = value.split('-').map(val => {
            return val.slice(0, 1).toUpperCase() + val.slice(1)
          })
          words.push(chunkstr.join('-'))
        } else {
          words.push(value.slice(0, 1).toUpperCase() + value.slice(1))
        }
      })
      return words.join(' ')
    }
    return str
  }

  get initials(): string {
    if (!this.displayName) return ''
    const parts = this.displayName.split(' ')
    let initials = parts[0].substring(0, 1).toUpperCase()

    if (parts.length > 1) {
      initials += parts[parts.length - 1].substring(0, 1).toUpperCase()
    }
    return initials
  }

  get profileColor() {
    let str = this.fullName
    if (this.phone) str += this.phone
    if (this.email) str += this.email
    let hash = 0
    if (str.length == 0) return hash
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash)
      hash = hash & hash // Convert to 32bit integer
    }
    const shortened = Math.abs(hash) % 360
    return 'hsl(' + shortened + ',35%, 60%)'
  }

  get profilePhoto(): string {
    return this.data.profilePhoto
  }

  set profilePhoto(profilePhoto: string) {
    this.data.profilePhoto = profilePhoto
    this.updatedData.profilePhoto = profilePhoto
  }

  get clio(): object {
    return this.data.clio
  }

  set clio(clio: object) {
    this.data.clio = clio
    this.updatedData.clio = clio
  }

  get clioId(): string {
    if (this.data['clio'] && this.data['clio']['id']) {
      return this.data['clio']['id']
    }
    return ''
  }

  set clioId(id: string) {
    const obj = this.data['clio'] || {}
    obj['id'] = id
    this.data['clio'] = obj
    this.updatedData['clio'] = obj
  }

  public clone() {
    const contact = new Contact(this.data)
    return contact
  }

  get facebookId(): string {
    if (this.data['facebook'] && this.data['facebook']['id']) {
      return this.data['facebook']['id']
    }
    return ''
  }

  set facebookId(id: string) {
    const obj = this.data['facebook'] || {}
    obj['id'] = id
    this.data['facebook'] = obj
    this.updatedData['facebook'] = obj
  }

  get poolId(): string {
    return this.data.poolId
  }

  set poolId(id: string) {
    this.data.poolId = id
    this.updatedData.poolId = id
  }

  get gclid(): string {
    return this.data.gclid
  }

  set gclid(gclid: string) {
    this.data.gclid = gclid
    this.updatedData.gclid = gclid
  }

  get sourceType(): string {
    return this.data.sourceType
  }

  set sourceType(type: string) {
    this.data.sourceType = type
    this.updatedData.sourceType = type
  }

  get keyword(): string {
    return this.data.keyword
  }

  set keyword(keyword: string) {
    this.data.keyword = keyword
    this.updatedData.keyword = keyword
  }

  get opportunities(): Array<IContactOpportunity> {
    if (!this.data.opportunities || !(this.data.opportunities instanceof Array))
      this.data.opportunities = []
    return this.data.opportunities
  }

  set opportunities(opportunities: Array<IContactOpportunity>) {
    this.data.opportunities = opportunities
    this.updatedData.opportunities = opportunities
  }

  get offers(): string[] {
    if (!this.data.offers) this.data.offers = []
    return this.data.offers
  }

  set offers(offers: string[]) {
    this.data.offers = offers
    this.updatedData.offers = offers
  }

  get products(): string[] {
    if (!this.data.products) this.data.products = []
    return this.data.products
  }

  set products(products: string[]) {
    this.data.products = products
    this.updatedData.products = products
  }

  set sessionId(sessionId: string) {
    this.data.sessionId = sessionId
    this.updatedData.sessionId = sessionId
  }

  get sessionId(): string {
    return this.data.sessionId
  }

  get phoneInfo(): PhoneInfo {
    return this.data.phoneInfo
  }


  set address1(address1: string | null) {
    this.data.address1 = address1
    this.updatedData.address1 = address1
  }

  get address1(): string | null {
    return this.data.address1
  }

  set phoneInfo(phoneInfo: PhoneInfo) {
    this.data.phoneInfo = phoneInfo
    this.updatedData.phoneInfo = phoneInfo
  }

  get dialogflowBotInfo(): DialogFlowBotInfo {
    return this.data.dialogflowBotInfo
  }

  set dialogflowBotInfo(dialogflowBotInfo: DialogFlowBotInfo) {
    this.data.dialogflowBotInfo = dialogflowBotInfo
    this.updatedData.dialogflowBotInfo = dialogflowBotInfo
  }

  public incrementSkipTrigger() {
    this.data.skipTrigger = firebase.firestore.FieldValue.increment(1)
  }

  public getDisplayOpportunities() {
    return Contact.formatOpportunities(this.opportunities)
  }

  get activeWorkflows(): string[] {
    return [
      ...(this.data.activeWorkflows || []),
      ...(this.data.activeWorkflows_2 || []),
    ]
  }

  get finishedWorkflows(): string[] {
    return [
      ...(this.data.finishedWorkflows || []),
      ...(this.data.finishedWorkflows_2 || []),
    ]
  }

  public get finishedWorkflowsDisplay() {
    const items = []
    try {
      const master =
        store &&
        store.state &&
        store.state.workflows &&
        store.state.workflows.workflows
          ? store.state.workflows.workflows.map(a => ({
              key: a.id,
              value: a.name,
            }))
          : []
      if (!master) return [...this.finishedWorkflows]
      this.finishedWorkflows.forEach(a => {
        const idx = master.findIndex(x => x.key === a)
        if (idx !== -1) items.push(master[idx].value)
      })
    } catch (err) {
      console.log(err)
    } finally {
      return items
    }
  }

  public get activeWorkflowsDisplay() {
    const items = []
    try {
      const master =
        store &&
        store.state &&
        store.state.workflows &&
        store.state.workflows.workflows
          ? store.state.workflows.workflows.map(a => ({
              key: a.id,
              value: a.name,
            }))
          : []

      if (!master) return [...this.activeWorkflows]
      this.activeWorkflows.forEach(a => {
        const idx = master.findIndex(x => x.key === a)
        if (idx !== -1) items.push(master[idx].value)
      })
    } catch (err) {
      console.log(err)
    } finally {
      return items
    }
  }

  public get offersDisplay() {
    // console.log('getDisplayOffers called')
    if (!this.offers || this.offers.length <= 0) return []

    const master = store.getters['membership/getOffers']
    if (!master || master.length <= 0) return [...this.offers]
    // console.log(this.offers)
    const items = []
    this.offers.forEach(a => {
      const idx = master.findIndex(x => x.key === a)
      if (idx !== -1) items.push(master[idx].value)
      //else items.push(a);
    })

    return items
  }

  public get productsDisplay() {
    // console.log('getDisplayProducts called')
    if (!this.products || this.products.length <= 0) return []

    const master = store.getters['membership/getProducts']
    if (!master || master.length <= 0) return [...this.products]
    // console.log(this.products)
    const items = []
    this.products.forEach(a => {
      const idx = master.findIndex(x => x.key === a)
      if (idx !== -1) items.push(master[idx].value)
      //else items.push(a);
    })

    return items
  }

  public static createNewWithVuex(vuexData) {
    return vuexData ? new Contact(cloneDeep(vuexData)) : undefined
  }

  public static formatOpportunities(
    opportunities: IContactOpportunity[]
  ): IFormattedOpportunity[] {
    if (!opportunities || !opportunities.length) return []
    const items = []
    opportunities.forEach(x => {
      const pipeline = store.getters['pipelines/getById'](x.pipelineId)
      const stage =
        pipeline && pipeline.stages
          ? pipeline.stages.find(a => a.id === x.pipelineStageId)
          : null
      if (pipeline)
        items.push({
          ...x,
          pipeline: pipeline.name,
          stage: stage ? stage.name : null,
        })
    })
    return items
  }

  public get isNewRecord(): boolean {
    return this.newRecord
  }

  get attributionSource(): { [key: string]: any } {
    return this.data.attributionSource
  }

  set attributionSource(attributionSource: { [key: string]: any }) {
    this.data.attributionSource = attributionSource
    this.updatedData.attributionSource = attributionSource
  }

  get lastAttributionSource(): { [key: string]: any } {
    return this.data.lastAttributionSource
  }

  set lastAttributionSource(attributionSource: { [key: string]: any }) {
    this.data.lastAttributionSource = attributionSource
    this.updatedData.lastAttributionSource = attributionSource
  }

  get dndSettings(): { [key in SendMessageTypes]: IDndSetting } {
    return this.data.dndSettings
  }

  set dndSettings(dndSettings: { [key in SendMessageTypes]: IDndSetting }) {
    this.data.dndSettings = dndSettings
    this.updatedData.dndSettings = dndSettings
  }


  get additionalEmails(): { email : string , valid ?:boolean , validEmailDate?: string  }[] {
    return this.data['additionalEmails']
  }

  set additionalEmails(emails: { email : string , valid ?:boolean , validEmailDate?: string  }[]) {
    this.data['additionalEmails'] =emails
    this.updatedData.additionalEmails = emails

  }

  get additionalPhones(): { phone : string , phoneLabel : PhoneLabels }[] {
    return this.data['additionalPhones']
  }

  set additionalPhones(phones: { phones:string , phoneLabel : PhoneLabels}[]) {
    this.data['additionalPhones'] =phones
    this.updatedData.additionalPhones = phones
  }

  get followers(): string[] {
    if (!this.data.followers) this.data.followers = []
    return this.data.followers
  }

  set followers(followers: string[]) {
    this.data.followers = followers
  }

  get isValidWhatsapp(): boolean {
    return this.data.isValidWhatsapp
  }


  set facebook(faceBookMetaData: FaceBookMetaData) {
    this.data.facebook = faceBookMetaData
    this.updatedData['facebook'] = faceBookMetaData
  }

  get facebook(): FaceBookMetaData {
    return this.data.facebook
  }

  set instagram(instagramMetaData: InstagramMetaData) {
    this.data.instagram = instagramMetaData
    this.updatedData['instagram'] = instagramMetaData
  }

  get instagram(): InstagramMetaData {
    return this.data.instagram
  }
}
